import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ShoppingCart, Trash2, Plus, Minus, Package, ChevronLeft } from 'lucide-react';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Breadcrumbs } from '../../components/navigation/NavigationHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CartPage() {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/commerce/cart`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setCart(data);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    try {
      const res = await fetch(`${API_URL}/api/commerce/cart/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        loadCart();
      }
    } catch (error) {
      alert('Failed to remove item');
    }
  };

  const proceedToCheckout = () => {
    if (cart.items.length === 0) return;
    navigate('/commerce/checkout', { state: { cart } });
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading cart...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShoppingCart className="w-8 h-8 text-blue-500" />
          Shopping Cart
        </h1>
        <Button variant="outline" onClick={() => navigate('/commerce/marketplace')}>Continue Shopping</Button>
      </div>

      {cart.items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">Your cart is empty</p>
            <Button onClick={() => navigate('/commerce/marketplace')}>Browse Products</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {cart.items.map(item => (
                  <div key={item.product_id} className="flex items-center gap-4 p-4 border rounded-lg">
                    <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                      {item.image ? (
                        <img src={item.image} alt={item.product_name} className="w-full h-full object-cover rounded" />
                      ) : (
                        <Package className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.product_name}</h3>
                      <p className="text-sm text-gray-600">{item.store_name}</p>
                      <p className="text-sm font-medium mt-1">{item.price} {item.currency}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">Qty: {item.quantity}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{(item.price * item.quantity).toFixed(2)} {item.currency}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => removeItem(item.product_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between text-lg">
                <span>Subtotal:</span>
                <span className="font-semibold">{cart.total.toFixed(2)} {cart.currency}</span>
              </div>
              <div className="flex justify-between text-2xl font-bold border-t pt-4">
                <span>Total:</span>
                <span>{cart.total.toFixed(2)} {cart.currency}</span>
              </div>
              <Button className="w-full" size="lg" onClick={proceedToCheckout}>
                Proceed to Checkout
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
